import type { ConsolidatedDocument } from "~/components/document/composables/useDocument";
import { useApi } from "~/composables/useApi";
import type { TalentContractToSignNotificationBody } from "@asap/shared/schemas/contract/list";
import type { DocumentSigningType, DocusealSendPayload, DocusealTemplatePayload } from "@asap/shared/types/docuseal";
import { convertUrlToBase64 } from "~/utils/encode";
import { useFile } from "./useFile";

/**
 * The DocuSeal integration allows for electronic signature of documents.
 * The process involves several steps:
 *
 * 1. Template Generation (creating the PDF on DocuSeal allowing for further signature).
 * 2. Submission Creation (sending the PDF to clients/talents, sending SMS/Email notifications and reminders)
 * 3. Signature Process (after a signature or a declined, a hook calls API and updates contract's state and send notifications that it's signed/declined)
 * 4. Archiving (cancel the signature process by archiving the template from step 1).
 *
 * For backend implementation check docuseal.routes.
 * If you have any trouble, don't forget to check their API documentation and Discord.
 */
export const useDocuSeal = () => {
  const { t } = useI18n();
  // Outside production the API simulates DocuSeal (DOCUSEAL_SIMULATED): nothing reaches a talent or a client.
  const { invoke } = useApi();

  const getContractDocument = async ({ contractId, documentId }: { contractId: string; documentId: string }) => {
    const response = await invoke({
      path: "contracts/:contractId/documents",
      method: "GET",
      params: { contractId },
      query: {},
    });
    if (!response.success) throw new Error(response.error.message);

    const document = response.data.find(({ id }) => id === documentId);
    if (!document) throw new Error(t("common.docuseal.documentNotFound"));
    return document;
  };

  const createDocusealSubmission = async (payload: DocusealSendPayload) => {
    const response = await invoke({
      path: "docuseal/send",
      method: "POST",
      body: payload,
    });

    if (!response.success) throw new Error(response.error.message);

    return response.data[0];
  };

  const archiveSubmission = async (submissionId: number) => {
    const answer = await invoke({
      path: "docuseal/archive",
      method: "POST",
      body: { submissionId },
    });
    if (!answer.success) throw new Error(answer.error.message);
  };

  const generateTemplate = async ({
    fileUrl,
    contractId,
    documentId,
    commercialConditionsId,
    type,
  }: {
    fileUrl: string;
    contractId?: string;
    documentId?: string;
    commercialConditionsId?: string;
    type: DocumentSigningType;
  }) => {
    const file = await convertUrlToBase64(fileUrl);
    const body: DocusealTemplatePayload = {
      file,
      commercialConditionsId,
      contractId,
      documentId,
      type,
    };

    const response = await invoke({
      path: "docuseal/generate-template",
      method: "POST",
      body,
    });
    if (!response.success) throw new Error(response.error.message);

    return response.data;
  };

  const sendTalentContract = async (payload: TalentContractToSignNotificationBody) => {
    const response = await invoke({
      path: "contract/notify-talent-contract-to-sign",
      method: "POST",
      body: payload,
      query: undefined,
    });

    if (!response.success) {
      if (response.error.message === "This service can not be triggered in test environment") {
        throw new Error("This service can not be triggered in test environment");
      } else {
        throw new Error(t("documents.docuseal.notifyTalentError"));
      }
    }
    return response.data;
  };

  const buildPayloadForSend = (
    {
      documentId,
      documentToSignId,
      recipientsEmail,
      type,
      additionalMessage,
    }: {
      documentId: string;
      documentToSignId?: string;
      recipientsEmail: string[];
      additionalMessage?: string;
      type: DocumentSigningType;
    },
    templateId: string | number
  ) => {
    if (typeof templateId === "string") templateId = parseInt(templateId);
    const payload: DocusealSendPayload = {
      templateId,
      documentId,
      documentToSignId,
      recipientsEmail,
      type,
      additionalMessage,
    };
    return payload;
  };

  const getTemplateId = async (params: { contractId: string; documentId: string }) => {
    const { docuseal_template_id } = await getContractDocument(params);
    return { data: { docuseal_template_id } };
  };

  const generateTemplateContract = async ({
    document,
    type,
    contractId,
  }: {
    document: ConsolidatedDocument;
    type: DocumentSigningType;
    contractId: string;
  }) => {
    const { addDownloadUrl } = useFile();

    const files = await addDownloadUrl(document.files);
    const file = files[0];
    await generateTemplate({
      fileUrl: file.downloadUrl,
      contractId,
      documentId: document.id,
      type,
    });
  };

  const generateTemplateCommercialConditions = async ({
    fileUrl,
    type,
    commercialConditionsId,
  }: {
    fileUrl: string;
    type: DocumentSigningType;
    commercialConditionsId: string;
  }) => {
    const template = await generateTemplate({
      fileUrl,
      commercialConditionsId,
      type,
    });
    return template;
  };

  const getSubmissionId = async (params: { contractId: string; documentId: string }) => {
    const { docuseal_submission_id } = await getContractDocument(params);
    return docuseal_submission_id;
  };

  // Read by document: the signed copy inserted by the DocuSeal webhook shares the same submission id
  const getSubmissionStatus = async (params: { contractId: string; documentId: string }) => {
    const { docuseal_is_archived, docuseal_declined_at } = await getContractDocument(params);
    return {
      isArchived: docuseal_is_archived === true,
      isDeclined: docuseal_declined_at !== null,
    };
  };

  return {
    archiveSubmission,
    getTemplateId,
    getSubmissionId,
    getSubmissionStatus,
    buildPayloadForSend,
    createDocusealSubmission,
    sendTalentContract,
    generateTemplateContract,
    generateTemplateCommercialConditions,
  };
};
